import { useState } from "react";
import { ChevronDown, ChevronRight, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type StatsExplainerVariant =
  | "sprzedaz"
  | "strona"
  | "lejki"
  | "koszyki"
  | "sciezki"
  | "sekcje"
  | "sesje"
  | "dzien"
  | "rolki"
  | "trendy"
  | "komentarze";

const COMMON = [
  "Sprzedaż i pieniądze bierzemy z EasyCart (prawdziwe opłacone zamówienia).",
  "Zachowanie na stronie (wizyty, koszyk, ścieżki) liczymy własnym skryptem. To nie jest Google Analytics.",
  "Liczby mogą różnić się od panelu EasyCart: inne definicje wizyt, blokady, wejścia prosto w koszyk. To normalne.",
  "Przy decyzjach o kasie polegaj na EasyCart. Nasz licznik służy do porównań ścieżek i treści.",
  "Dzień = kalendarz polski (jak w panelu EasyCart). Poniżej ~15 wizyt procenty bywają mylące.",
];

const EXTRA: Record<StatsExplainerVariant, string[]> = {
  sprzedaz: [
    "Kafelki na górze to sumy przychodu z EasyCart. Anulowane nie wchodzą.",
    "Wykres trendu pokazuje przychód dzień po dniu (nie pojedyncze transakcje).",
  ],
  strona: [
    "Karta „Kanały (jak w GA4)” używa tych samych nazw co Google; licz po UTM i referrerze.",
    "Na górze karty: podział Płatne wejścia (?via=ad / Social płatny) vs Pozostałe.",
    "„Nowe wizyty” ≈ GA „nowi użytkownicy”, ale to nadal nasz licznik sesji, nie kopia GA.",
    "Filtr ?via= i UTM zostają dla precyzji (który link / kampania).",
    "Blok Google / Clarity na dole to stare liczniki, tylko do porównania.",
  ],
  lejki: [
    "Kolumny porównują grupy (ruch, nowi vs powracający, link). Szukaj największej ucieczki między krokami.",
  ],
  koszyki: [
    "„Wejścia do koszyka” to nasz licznik, nie „Sesje zakupowe” z EasyCart.",
    "Zielony krok „Zakup” w kolumnie produktu = opłacone zamówienia EasyCart dla tego SKU.",
  ],
  sciezki: [
    "Ścieżka = kolejność stron w jednej wizycie przed koszykiem lub wyjściem.",
    "To widok makro (top ścieżki). Szczegóły pojedynczej sesji znajdziesz w zakładce „Sesje zakupowe”.",
  ],
  sesje: [
    "Sesja zakupowa = wizyta, która otworzyła koszyk (checkout). Liczymy zakupy i porzucenia.",
    "To NIE jest licznik „Sesje zakupowe” z panelu EasyCart.",
    "Kliknij wiersz, żeby zobaczyć timeline zdarzeń. Bez e-maili i pełnych identyfikatorów (PII).",
    "Część zakupów ma słabą atrybucję (krótki timeline): kwota z EasyCart jest prawdziwa, ścieżka landingu może być pusta.",
  ],
  sekcje: [
    "Każda kolumna to jedna strona. Procent = ile wizyt dojechało do sekcji.",
    "Obok procentu: liczba kliknięć CTA z tego etapu (kto poszedł dalej).",
    "Pod paskiem: które przyciski były klikane najczęściej w tej sekcji.",
  ],
  dzien: [
    "Analiza jednego dnia kalendarzowego (czas polski). Wybierz Dziś lub Wczoraj.",
    "Wykres godzinowy pokazuje, o której godzinie były wizyty, koszyki i sprzedaż.",
    "Przychód bierzemy z EasyCart. Wizyty i koszyki z naszego licznika na stronie.",
    "Płatne wejścia = ?via=ad lub Social płatny. Reszta to pozostałe kanały.",
  ],
  rolki: [
    "Linia czasu: ruch i sprzedaż osobno, a pod spodem przyrost views IG+FB+karuzele jako suma.",
    "Przyrost = nowe views między fetchami Graph (godzina odczytu). KPI i tabela = aktualny stan.",
    "Tooltip „Ruch i sprzedaż”: top ?via= z sesji tej godziny (co przyprowadziło wejścia) + publikacje z tej godziny.",
    "Hasła = krótkie komentarze mama/matka/pakiet/ulga. To nie jest live API ManyChat.",
    "Pobieramy tylko Instagram i Facebook przez Graph. TikTok pomijamy (brak Graph, bez Apify).",
  ],
  trendy: [
    "Lejek: wyświetlenia (przyrost Graph) → wejścia → koszyki → zakupy EasyCart w tygodniu lub miesiącu.",
    "Views i sesje to różne populacje (social ≠ site). To skala ciśnienia i konwersji okna, nie ta sama osoba.",
    "Δ% porównuje z poprzednim pełnym tygodniem ISO / miesiącem kalendarzowym (czas PL).",
    "Topka: najpierw zakupy z ?via= (twarde), potem blisko w czasie (±2h), na końcu korelacja ruchu.",
  ],
  komentarze: [
    "Liczymy komentarze z plików kanałów (IG, FB, YT, TT), nie ze starego snapshotu unified.",
    "CTA = krótkie hasło Mama/Matka. To nie jest live API ManyChat.",
    "Klasy i bóle to te same reguły co w analizach 2026-08 i katalogu karuzel. Nie łączymy komentarzy z zamówieniami.",
    "Pokrycie = dni z zapisem i odsetek postów z reply Kasi. To nie jest skrzynka do odpowiadania.",
    "TikTok jest zimnym dumpem. IG i FB są źródłem prawdy na dziś.",
  ],
};

export default function StatsExplainer({
  variant,
  className,
}: {
  variant: StatsExplainerVariant;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const points = [...COMMON, ...(EXTRA[variant] || [])];

  return (
    <Card className={cn("border-dashed", className)}>
      <CardHeader className="flex flex-row items-center justify-between gap-3 py-3">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Jak czytać te statystyki</CardTitle>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? (
            <>
              Zwiń <ChevronDown className="ml-1 h-4 w-4" />
            </>
          ) : (
            <>
              Rozwiń <ChevronRight className="ml-1 h-4 w-4" />
            </>
          )}
        </Button>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
            {points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  );
}
